import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Star, Heart, Sparkles, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import mockData from '../utils/mock';

/* ------- RK Carousel widget (renders inside Testimonials) ------- */
function ReviewsWidget() {
  useEffect(() => {
    let tries = 0;
    function init() {
      if (window.romw && typeof window.romw.init === 'function') {
        try { window.romw.init(); } catch {}
      } else if (tries++ < 25) {
        setTimeout(init, 300);
      }
    }
    init();
  }, []);

  return (
    <section
      aria-label="Customer reviews"
      className="flex justify-center"
      style={{ marginTop: 24 }}
    >
      <div
        id="rk-reviews"
        data-romw-token="Euf6K0zVt4Vfv1x4Gi6FY9pYlfWfTKKafyhUVwGkhyqTJiWkF3"
        data-romw-lazy
        style={{
          width: '100%',
          maxWidth: 1100,
          minHeight: 520,
          boxSizing: 'border-box',
          border: 'none',
          outline: 'none',
          boxShadow: 'none'
        }}
      />
      {/* Hard-kill any borders/outlines/shadows the widget or iframe might add */}
      <style>{`
        #rk-reviews, #rk-reviews * {
          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
        }
        #rk-reviews iframe,
        iframe[data-romw-embed],
        iframe#iFrameResizer0,
        iframe#iFrameResizer1 {
          display:block !important;
          width:100% !important;
          min-height:520px !important;
          border:0 !important;
          outline:0 !important;
          box-shadow:none !important;
          background: transparent !important;
        }
        /* Remove any default UA focus/selection decorations on the outer wrapper */
        section[aria-label="Customer reviews"] {
          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
        }
        /* Keep a simple loading hint while the iframe boots */
        #rk-reviews:empty::before {
          content: "Loading Google reviews…";
          display: block;
          text-align: center;
          opacity: .6;
          margin-top: 1rem;
          font: 16px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
        }
      `}</style>
    </section>
  );
}
/* --------------------------------------------------------------- */

const Home = () => {
  const [activeService, setActiveService] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:bg-white focus:text-rose-600 focus:px-4 focus:py-2 focus:rounded focus:shadow-lg"
        >
          Skip to main content
        </a>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <a href="#home" aria-label="Go to top">
              <img 
                src="https://customer-assets.emergentagent.com/job_45bf91d8-03ab-4216-8d9b-87abe81b5417/artifacts/lit9lqjr_KCNails.png"
                alt="KC Nails UKR Logo"
                className="h-16 w-16 object-contain"
              />
            </a>
          </div>
          <nav className="hidden md:flex space-x-8" aria-label="Primary navigation">
            <a href="#services" className="text-gray-700 hover:text-rose-600 transition-colors font-medium">Services</a>
            <a href="#gallery" className="text-gray-700 hover:text-rose-600 transition-colors font-medium">Gallery</a>
            <a href="#about" className="text-gray-700 hover:text-rose-600 transition-colors font-medium">About</a>
            <a href="#promotions" className="text-gray-700 hover:text-rose-600 transition-colors font-medium">Promotions</a>
            <a href="#contact" className="text-gray-700 hover:text-rose-600 transition-colors font-medium">Contact</a>
          </nav>
          <a href="tel:+15874042400" className="hidden md:block">
            <Button className="bg-rose-600 hover:bg-rose-700 text-white">
              <Phone className="mr-2 h-4 w-4" />
              Book Now
            </Button>
          </a>
          <button 
            className="md:hidden p-2"
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="space-y-1.5">
              <span className="block w-6 h-0.5 bg-gray-900"></span>
              <span className="block w-6 h-0.5 bg-gray-900"></span>
              <span className="block w-6 h-0.5 bg-gray-900"></span>
            </div>
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4" id="mobile-navigation">
            <nav className="flex flex-col space-y-3 px-4" aria-label="Mobile navigation">
              <a href="#services" className="text-gray-700 hover:text-rose-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#gallery" className="text-gray-700 hover:text-rose-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
              <a href="#about" className="text-gray-700 hover:text-rose-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#promotions" className="text-gray-700 hover:text-rose-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Promotions</a>
              <a href="#contact" className="text-gray-700 hover:text-rose-600 transition-colors font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            </nav>
          </div>
        )}
      </header>

      <main id="main-content" className="pt-24">
        {/* Hero Section */}
        <section id="home" className="relative min-h-[calc(100vh-96px)] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_45bf91d8-03ab-4216-8d9b-87abe81b5417/artifacts/38262qtk_image.png"
              alt="Hand-painted Calgary nail art by KC Nails UKR"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/45"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
            <div className="max-w-2xl space-y-6">
              <div className="flex items-center space-x-2 animate-fadeIn">
                <Sparkles className="h-6 w-6 text-rose-300" />
                <span className="text-rose-200 text-sm uppercase tracking-wider font-medium">Calgary Nail Art & Gel Specialists</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold leading-tight animate-fadeInUp">
                Calgary&apos;s Premier <span className="text-rose-300">Nail Art Studio</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 leading-relaxed animate-fadeInUp">
                KC Nails UKR delivers long-lasting gel manicures, pedicures, and bespoke hand-painted nail art for clients across Calgary, Alberta.
              </p>
              <div className="flex flex-wrap gap-4 animate-fadeInUp">
                <a href="tel:+15874042400">
                  <Button size="lg" className="bg-rose-600 hover:bg-rose-700 hover:shadow-2xl text-white px-8 py-6 text-lg transition-all duration-300">
                    <Phone className="mr-2 h-5 w-5" />
                    Call The Calgary Studio
                  </Button>
                </a>
                <a href="#services">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-rose-900 px-8 py-6 text-lg transition-all duration-300"
                  >
                    View Calgary Services
                  </Button>
                </a>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 text-left animate-fadeInUp">
                <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <MapPin className="h-6 w-6 text-rose-200 mt-1" />
                  <div>
                    <p className="text-sm uppercase tracking-wide text-rose-100">Serving Calgary</p>
                    <p className="text-base text-gray-100">
                      Downtown, Beltline, Mission, Inglewood, and neighbouring communities across Calgary.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <Clock className="h-6 w-6 text-rose-200 mt-1" />
                  <div>
                    <p className="text-sm uppercase tracking-wide text-rose-100">Flexible Hours</p>
                    <p className="text-base text-gray-100">
                      Monday–Friday 9am–7pm · Saturday 10am–6pm · Sunday by appointment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Signature Calgary Nail Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover gel manicures, pedicures, strengthening overlays, and detailed nail art tailored for Calgary lifestyles and our changing prairie seasons.
            </p>
          </div>

          {/* Hands Services */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-rose-900 mb-8 flex items-center">
              <Heart className="mr-3 h-8 w-8 text-rose-600" />
              Hands (All Include Manicure)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockData.services.hands.map((service, index) => (
                <Card 
                  key={index} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-rose-100"
                  onClick={() => setActiveService(service.name)}
                >
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h4>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-rose-600">{service.price}</span>
                      <span className="text-sm text-gray-500">{service.duration}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Feet Services */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-rose-900 mb-8 flex items-center">
              <Sparkles className="mr-3 h-8 w-8 text-rose-600" />
              Feet (All Include Pedicure)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockData.services.feet.map((service, index) => (
                <Card 
                  key={index} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-rose-100"
                >
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h4>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-rose-600">{service.price}</span>
                      <span className="text-sm text-gray-500">{service.duration}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Extras */}
          <div>
            <h3 className="text-3xl font-bold text-rose-900 mb-8 flex items-center">
              <Star className="mr-3 h-8 w-8 text-rose-600" />
              Extras
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {mockData.services.extras.map((service, index) => (
                <Card 
                  key={index} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-rose-100"
                >
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h4>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-rose-600">{service.price}</span>
                      <span className="text-sm text-gray-500">{service.duration}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of Calgary nail artistry, from minimalist gel finishes to high-impact statement designs for events and everyday wear.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {mockData.gallery.map((image, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-lg aspect-square cursor-pointer"
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white p-4 text-sm">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1632345031435-8727f6897d53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtYW5pY3VyZXxlbnwwfHx8fDE3NjAwNDI4NjF8MA&ixlib=rb-4.1.0&q=85"
                alt="About KC Nails UKR"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">About KC Nails UKR</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {mockData.about.story}
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {mockData.about.mission}
              </p>
              <div className="space-y-4">
                {mockData.about.values.map((value, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Star className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{value.title}</h4>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      <section id="promotions" className="py-20 bg-gradient-to-r from-rose-100 via-pink-100 to-rose-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Special Promotions</h2>
            <p className="text-xl text-gray-600">Exclusive savings for new and returning Calgary clients.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mockData.promotions.map((promo, index) => (
              <Card key={index} className="bg-white shadow-xl border-rose-200">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-rose-900">{promo.title}</h3>
                    <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {promo.discount}
                    </span>
                  </div>
                  <p className="text-gray-700 text-lg mb-6">{promo.description}</p>
                  <div className="space-y-2">
                    {promo.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 flex items-center">
                        <span className="text-rose-600 mr-2">•</span>
                        {detail}
                      </p>
                    ))}
                  </div>
                  {promo.validUntil && (
                    <p className="mt-4 text-sm text-gray-500">
                      Valid until: {promo.validUntil}
                    </p>
                  )}
                  <a href="tel:+15874042400" className="block mt-6">
                    <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white">
                      Book This Offer
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Authentic feedback from Calgary clients who trust KC Nails UKR</p>
          </div>

          {/* ReputationKarma Carousel */}
          <ReviewsWidget />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Book Your Calgary Appointment</h2>
            <p className="text-xl text-gray-600">Reach out to reserve your spot or ask about custom nail art for events around Calgary.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Studio Location</h4>
                    <p className="text-lg text-gray-700">{mockData.contact.address}</p>
                    <ul className="mt-3 space-y-1 text-gray-600 list-disc list-inside">
                      {mockData.contact.serviceArea.map((area) => (
                        <li key={area}>{area}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <a href="tel:+15874042400" className="text-lg text-gray-700 hover:text-rose-600 transition-colors">
                      +1 587 404 2400
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <a href="mailto:kcnailsukr@gmail.com" className="text-lg text-gray-700 hover:text-rose-600 transition-colors">
                      kcnailsukr@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Hours</h4>
                    <div className="text-gray-700">
                      {mockData.contact.hours.map((hour, index) => (
                        <p key={index}>{hour}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="shadow-xl border-rose-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Ready to Book?</h3>
                <p className="text-gray-700 mb-6">
                  Contact us today to secure your Calgary appointment and experience bespoke nail artistry that lasts.
                </p>
                <a href="tel:+15874042400" className="block">
                  <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white text-lg py-6">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now: +1 587 404 2400
                  </Button>
                </a>
                <div className="mt-6">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578609285560"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-rose-600 hover:text-rose-700 font-semibold"
                  >
                    Follow Us on Facebook →
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-rose-400 mb-4">KC Nails UKR</h3>
              <p className="text-gray-400">
                Where art meets beauty in Calgary. Experience luxury nail care with creative, personalized designs tailored to Alberta lifestyles.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-rose-400 transition-colors">Services</a></li>
                <li><a href="#gallery" className="text-gray-400 hover:text-rose-400 transition-colors">Gallery</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-rose-400 transition-colors">About</a></li>
                <li><a href="#promotions" className="text-gray-400 hover:text-rose-400 transition-colors">Promotions</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-rose-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
              <div className="space-y-2">
                <a href="tel:+15874042400" className="text-gray-400 hover:text-rose-400 transition-colors block">
                  Phone: +1 587 404 2400
                </a>
                <a href="mailto:kcnailsukr@gmail.com" className="text-gray-400 hover:text-rose-400 transition-colors block">
                  Email: kcnailsukr@gmail.com
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=61578609285560"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rose-400 hover:text-rose-300 transition-colors inline-block mt-2"
                >
                  Facebook →
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 KC Nails UKR. Proudly serving Calgary, Alberta.</p>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-rose-600 hover:bg-rose-700 text-white p-4 rounded-full shadow-2xl z-50 transition-all duration-300 hover:scale-110"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default Home;
